import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  useTheme,
  alpha,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider
} from "@mui/material";
import {
  Lock,
  Person,
  Visibility,
  VisibilityOff,
  LockOpen
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../config/msalConfig";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [azureLoading, setAzureLoading] = useState(false);
  const [isProcessingAzureCallback, setIsProcessingAzureCallback] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithAzure, isAuthenticated } = useAuth();
  const { instance, inProgress } = useMsal();
  const theme = useTheme();

  // Verificar si Azure AD está configurado
  const isAzureAdConfigured = !!(
    process.env.REACT_APP_AZURE_AD_CLIENT_ID &&
    process.env.REACT_APP_AZURE_AD_TENANT_ID
  );

  // Redirigir al dashboard o a la página solicitada si el usuario ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Redirigir a la página que intentaba acceder, o al dashboard por defecto
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Detectar si la sesión expiró o errores de Azure AD
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('sessionExpired') === 'true') {
      setSessionExpiredMessage("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.");
      window.history.replaceState({}, document.title, '/login');
    }

    // Manejar errores de Azure AD
    const azureError = params.get('error');
    if (azureError) {
      const errorMessages = {
        'user_inactive': 'Usuario inactivo. Contacte al administrador.',
        'invalid_account': 'No se pudo obtener información de la cuenta de Azure AD.',
        'database_error': 'Error al procesar la autenticación. Intente nuevamente.',
        'azure_auth_failed': 'Error al autenticar con Microsoft. Intente nuevamente.',
        'azure_not_configured': 'Autenticación con Microsoft no está configurada.'
      };

      setError(errorMessages[azureError] || 'Error al iniciar sesión con Microsoft');
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location]);

  // Detectar si estamos procesando un callback de Azure AD
  useEffect(() => {
    const checkAzureCallback = async () => {
      // Si hay parámetros de Azure AD en la URL (code, state, etc.)
      const params = new URLSearchParams(window.location.search);
      const hasAzureParams = params.has('code') && params.has('state');

      // Solo mostrar loading si:
      // 1. Hay parámetros de Azure en la URL (code + state juntos)
      // 2. O MSAL está activamente procesando algo (handleRedirect, login, etc.)
      if (hasAzureParams && inProgress !== 'none') {
        setIsProcessingAzureCallback(true);

        // Timeout de seguridad: después de 10 segundos, ocultar el loading
        const timeout = setTimeout(() => {
          setIsProcessingAzureCallback(false);
        }, 10000);

        return () => clearTimeout(timeout);
      } else {
        setIsProcessingAzureCallback(false);
      }
    };

    checkAzureCallback();
  }, [inProgress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(username, password);

      // Redirigir a la página que intentaba acceder, o al dashboard por defecto
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLogin = async () => {
    setError("");
    setAzureLoading(true);

    try {
      // Asegurarse de que MSAL esté inicializado antes de usarlo
      await instance.initialize();

      // Iniciar login con Azure AD usando redirect (en vez de popup)
      // Esto evita problemas con COOP headers
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error("Error en login de Azure:", err);
      setError(err.message || "Error al iniciar sesión con Azure AD");
      setAzureLoading(false);
    }
  };

  // Si estamos procesando el callback de Azure AD, mostrar loading screen
  if (isProcessingAzureCallback) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.dark} 100%)`,
        }}
      >
        <CircularProgress size={60} sx={{ color: "white" }} />
        <Typography variant="h6" sx={{ color: "white", fontWeight: 500 }}>
          Procesando autenticación de Azure AD...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.dark} 100%)`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.light, 0.3)} 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${alpha(theme.palette.secondary.light, 0.3)} 0%, transparent 50%)`,
        },
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: 5,
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.background.paper, 0.98),
            backdropFilter: "blur(20px)",
            boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.3)}`,
          }}
        >
          {/* Logo/Icon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <LockOpen sx={{ fontSize: 40, color: "white" }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 1,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Bienvenido
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 4, fontWeight: 500 }}
          >
            Ingresa tus credenciales para acceder
          </Typography>

          {/* Session Expired Alert */}
          {sessionExpiredMessage && (
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                "& .MuiAlert-icon": {
                  color: theme.palette.warning.main,
                },
              }}
              onClose={() => setSessionExpiredMessage("")}
            >
              {sessionExpiredMessage}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                "& .MuiAlert-icon": {
                  color: theme.palette.error.main,
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              disabled={loading}
              required
              autoFocus
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                  },
                  "&.Mui-focused": {
                    backgroundColor: theme.palette.background.default,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              disabled={loading}
              required
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                  },
                  "&.Mui-focused": {
                    backgroundColor: theme.palette.background.default,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || azureLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: "1rem",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                textTransform: "none",
                "&:hover": {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  background: theme.palette.action.disabledBackground,
                },
                transition: "all 0.3s",
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Iniciar Sesión"
              )}
            </Button>
          </form>

          {/* Divider con "O" y Botón de Azure AD - Solo si está configurado */}
          {isAzureAdConfigured && (
            <>
              {/* Divider con "O" */}
              <Box sx={{ my: 3, display: "flex", alignItems: "center", gap: 2 }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  O
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>

              {/* Botón de Azure AD */}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAzureLogin}
                disabled={loading || azureLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  borderColor: theme.palette.divider,
                  borderWidth: 2,
                  color: theme.palette.text.primary,
                  textTransform: "none",
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.5)
                    : "white",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    borderColor: theme.palette.action.disabled,
                    backgroundColor: theme.palette.action.disabledBackground,
                  },
                  transition: "all 0.3s",
                }}
              >
                {azureLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      component="img"
                      src="https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_mssymbol_19.svg"
                      alt="Microsoft"
                      sx={{ width: 20, height: 20 }}
                    />
                    <Typography sx={{ fontWeight: 600 }}>
                      Iniciar sesión con Microsoft
                    </Typography>
                  </Box>
                )}
              </Button>
            </>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              © 2025 Garantías App. Todos los derechos reservados.
            </Typography>
          </Box>
        </Paper>

        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: alpha(theme.palette.secondary.light, 0.1),
            filter: "blur(60px)",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -100,
            left: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: alpha(theme.palette.primary.light, 0.1),
            filter: "blur(80px)",
            zIndex: 0,
          }}
        />
      </Container>
    </Box>
  );
};

export default Login;
